import type { StripeError } from '@stripe/stripe-js';

// Friendly wording for Stripe.js client-side errors (3D Secure confirmation,
// card-save / SetupIntent failures). Stripe’s own `error.message` strings are
// usually readable but unbranded and occasionally technical, so we translate the
// common decline/validation codes ourselves and keep a warm generic fallback.
// Mirrors the backend map in `stripe.service.ts` so users see consistent copy.
const FRIENDLY: Record<string, string> = {
  insufficient_funds: 'Your card has insufficient funds. Please try a different card.',
  expired_card: 'Your card has expired — please use a different card.',
  incorrect_cvc: 'Your card’s security code (CVC) is incorrect. Please check and try again.',
  invalid_cvc: 'Your card’s security code (CVC) is incorrect. Please check and try again.',
  incorrect_number: 'Your card number appears to be incorrect. Please check and try again.',
  invalid_number: 'Your card number appears to be incorrect. Please check and try again.',
  invalid_expiry_month: 'The card’s expiration month is invalid. Please check and try again.',
  invalid_expiry_year: 'The card’s expiration year is invalid. Please check and try again.',
  incorrect_zip: 'The postal code doesn’t match your card. Please check and try again.',
  card_declined: 'Your card was declined. Please try a different card or contact your bank.',
  do_not_honor: 'Your bank declined the payment. Please try a different card or contact your bank.',
  generic_decline: 'Your card was declined. Please try a different card or contact your bank.',
  lost_card: 'Your card was declined. Please use a different card.',
  stolen_card: 'Your card was declined. Please use a different card.',
  pickup_card: 'Your card was declined. Please use a different card or contact your bank.',
  authentication_required: 'Your bank needs to verify this payment. Please complete the verification and try again.',
  authentication_failed: 'We couldn’t verify this payment with your bank. Please try again.',
  processing_error: 'There was a problem processing your card. Please try again in a moment.',
  card_velocity_exceeded: 'This card has been used too many times recently. Please try another card.',
  currency_not_supported: 'Your card doesn’t support this currency. Please try a different card.',
  card_not_supported: 'Your card doesn’t support this type of purchase. Please try a different card.',
};

/**
 * Map a Stripe.js `StripeError` to a friendly, on-brand message.
 * @param err The error returned by `confirmCardPayment` / `confirmSetup`.
 * @param fallback Message used when the error code isn’t one we recognise.
 */
export function friendlyStripeError(err: StripeError | undefined, fallback: string): string {
  if (!err) {
    return fallback;
  }

  const code = err.decline_code || err.code || '';

  if (code && FRIENDLY[code]) {
    return FRIENDLY[code];
  }

  // Validation errors (e.g. incomplete card fields) carry safe, specific copy.
  if (err.type === 'validation_error' && err.message) {
    return err.message;
  }

  return fallback;
}
